import omitBy from "lodash/omitBy";
import uniqBy from "lodash/uniqBy";
import sortBy from "lodash/sortBy";
import { useEffect, useMemo, useState } from "react";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { AgendaProps } from "./AgendaProps";
import {
    convertToScheduledSessions,
    EventList,
    EventListContainer,
} from "@kaltura/mediaspace-shared-event-platform-event-list";
import { NoSessions } from "@kaltura/mediaspace-shared-ui";
import { FilterByTags } from "@kaltura/mediaspace-shared-types";
import {
    FilterBar,
    filterSessions,
    FiltersValues,
    getFiltersValuesFromQueryString,
} from "@kaltura/mediaspace-shared-search-with-filter-tags";
import { styled } from "@mui/material";

const StyledFilterBarContainer = styled("div")(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

/**
 * Agenda Component - presents, search and filters sessions
 */
export function Agenda(props: AgendaProps) {
    const {
        sessions = [],
        showDateFilter,
        filters = [],
        disabledMode = false,
        headingLevel,
        calendarEntriesToken,
    } = props;

    const [inputValue, setInputValue] = useState<string>("");
    const [filterValues, setFilterValues] = useState<FiltersValues>({});
    const [allFilters, setAllFilters] = useState<FilterByTags[]>(filters);

    const config = useConfig();
    const { dateFormats } = config.application;

    const handleInputChange = (value: string) => {
        // set input value
        setInputValue(value);
    };

    const handleInputReset = () => {
        // clear input value
        setInputValue("");
    };

    const handleFilterChange = (param: string, values: string[]) => {
        // new filters values, without empty filters
        setFilterValues((filterValues) => omitBy({ ...filterValues, [param]: values }, (elem) => elem.length === 0));
    };

    const handleClearBubbles = () => {
        // clear all filters
        setFilterValues({});
    };

    // set start date label on all sessions
    const scheduledSessions = useMemo(
        () => convertToScheduledSessions(sessions, dateFormats.longDateNoYear),
        [sessions, dateFormats.longDateNoYear]
    );

    const filteredSessions = useMemo(
        () => filterSessions(scheduledSessions, inputValue, filterValues),
        [scheduledSessions, inputValue, filterValues]
    );

    useEffect(() => {
        const getUniqueDates = () => {
            // extract start dates from sessions
            const allDates = scheduledSessions.map((session) => ({
                timestamp: session.schedulingData.start.timestamp,
                label: session.schedulingData.label,
            }));

            // sort and drop duplicates
            return sortBy(uniqBy(allDates, "label"), (date) => date.timestamp);
        };

        // compose filters with/out date filter
        const allFilters = showDateFilter
            ? [
                  {
                      id: "date",
                      label: translate("Select date"),
                      param: "date",
                      tags: getUniqueDates().map((date) => date.label),
                      countSelected: false,
                  },
                  ...filters,
              ]
            : filters;

        setAllFilters(allFilters);

        const filterValuesFromQueryString = getFiltersValuesFromQueryString(allFilters);
        Object
            .entries(filterValuesFromQueryString)
            .forEach(([key, value]) => {
                handleFilterChange(key, value);
            });
    }, []);

    return (
        <div className="kms-ds-agenda">
            <StyledFilterBarContainer>
                <FilterBar
                    onInputChange={handleInputChange}
                    onInputReset={handleInputReset}
                    onClearBubbles={handleClearBubbles}
                    onFilterChange={handleFilterChange}
                    filters={allFilters}
                    values={filterValues}
                />
            </StyledFilterBarContainer>
            {filteredSessions.length === 0 && <NoSessions />}
            {filteredSessions.length > 0 && (
                <EventListContainer
                    sessions={filteredSessions}
                    disabledMode={disabledMode}
                    headingLevel={headingLevel}
                    calendarEntriesToken={calendarEntriesToken}
                    eventListComponent={EventList}
                />
            )}
        </div>
    );
}

export default Agenda;
